/**
 * CategoryAccordion - Collapsible section for a settings category
 */
import { useState, useCallback, useMemo } from "react";
import { ChevronRight, Save01, RefreshCw01 } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { SettingRow } from "./setting-row";
import { SchedulerJobsPanel } from "./scheduler-jobs-panel";
import { CATEGORY_CONFIG } from "../category-config";
import type { Setting, SettingCategory } from "@/types/api/settings";
import { cx } from "@/utils/cx";

interface CategoryAccordionProps {
    category: SettingCategory;
    settings: Setting[];
    isExpanded: boolean;
    onToggle: () => void;
    onSave: (values: Record<string, unknown>) => Promise<void>;
    onReset: () => void;
    isSaving: boolean;
}

export function CategoryAccordion({
    category,
    settings,
    isExpanded,
    onToggle,
    onSave,
    onReset,
    isSaving,
}: CategoryAccordionProps) {
    // Local state for edited values
    const [editedValues, setEditedValues] = useState<Record<string, unknown>>({});
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

    const config = CATEGORY_CONFIG[category];
    const Icon = config.icon;

    // Check if any values have been changed
    const isDirty = useMemo(() => {
        return Object.keys(editedValues).some((key) => {
            const setting = settings.find((s) => s.key === key);
            if (!setting) return false;
            return editedValues[key] !== setting.value;
        });
    }, [editedValues, settings]);

    // Get current value for a setting (edited or original)
    const getValue = useCallback(
        (setting: Setting) => {
            if (setting.key in editedValues) {
                return editedValues[setting.key];
            }
            return setting.value;
        },
        [editedValues]
    );

    // Handle value change
    const handleChange = useCallback((key: string, value: unknown) => {
        setEditedValues((prev) => ({ ...prev, [key]: value }));
        // Clear validation error on change
        setValidationErrors((prev) => {
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    // Validate values before saving
    const validateValues = useCallback(() => {
        const errors: Record<string, string> = {};

        for (const setting of settings) {
            const value = getValue(setting);
            const rules = setting.validation_rules;

            if (!rules) continue;

            // Validate min/max for numbers
            if (setting.value_type === "integer" || setting.value_type === "float") {
                const numValue = Number(value);
                if (rules.min !== undefined && numValue < rules.min) {
                    errors[setting.key] = `Minimum value is ${rules.min}`;
                }
                if (rules.max !== undefined && numValue > rules.max) {
                    errors[setting.key] = `Maximum value is ${rules.max}`;
                }
            }

            // Validate pattern for strings
            if (setting.value_type === "string" && rules.pattern && value) {
                const regex = new RegExp(rules.pattern);
                if (!regex.test(String(value))) {
                    errors[setting.key] = `Value doesn't match required format`;
                }
            }
        }

        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [settings, getValue]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!validateValues()) return;

        // Only send changed values
        const changedValues: Record<string, unknown> = {};
        for (const key of Object.keys(editedValues)) {
            const setting = settings.find((s) => s.key === key);
            if (setting && editedValues[key] !== setting.value) {
                changedValues[key] = editedValues[key];
            }
        }

        if (Object.keys(changedValues).length === 0) return;

        await onSave(changedValues);
        // Clear edited values on successful save
        setEditedValues({});
    }, [editedValues, settings, validateValues, onSave]);

    // Handle reset
    const handleReset = useCallback(() => {
        setEditedValues({});
        setValidationErrors({});
        onReset();
    }, [onReset]);

    return (
        <div className="rounded-xl bg-primary shadow-xs ring-1 ring-secondary overflow-hidden mb-4">
            {/* Accordion Header */}
            <div className="w-full flex items-center justify-between p-4">
                <button
                    type="button"
                    onClick={onToggle}
                    className="flex-1 flex items-center gap-3 hover:bg-primary_hover transition-colors -ml-4 -mr-4 px-4 py-0"
                    aria-expanded={isExpanded}
                >
                    <ChevronRight
                        className={cx(
                            "size-5 text-fg-quaternary transition-transform duration-200",
                            isExpanded && "rotate-90"
                        )}
                    />
                    <Icon className="size-5 text-fg-quaternary" />
                    <span className="font-semibold text-primary">{config.displayName}</span>
                    {isDirty && (
                        <Badge color="warning" size="sm">
                            Unsaved
                        </Badge>
                    )}
                </button>

                {/* Action buttons (always visible) */}
                <div className="flex items-center gap-2">
                    <Button
                        color="secondary"
                        size="sm"
                        iconLeading={RefreshCw01}
                        onClick={handleReset}
                        isDisabled={!isDirty}
                    >
                        Reset
                    </Button>
                    <Button
                        color="primary"
                        size="sm"
                        iconLeading={Save01}
                        onClick={handleSave}
                        isDisabled={!isDirty}
                        isLoading={isSaving}
                    >
                        Save
                    </Button>
                </div>
            </div>

            {/* Accordion Content */}
            {isExpanded && (
                <div className="border-t border-secondary p-6">
                    {/* Category description */}
                    <p className="text-sm text-tertiary mb-4">{config.description}</p>

                    {/* Settings list */}
                    <div className="space-y-1">
                        {settings.length === 0 ? (
                            <p className="text-tertiary text-sm py-4 text-center">No settings in this category</p>
                        ) : (
                            settings.map((setting) => (
                                <SettingRow
                                    key={setting.key}
                                    setting={setting}
                                    value={getValue(setting)}
                                    onChange={(value) => handleChange(setting.key, value)}
                                    validationError={validationErrors[setting.key]}
                                />
                            ))
                        )}
                    </div>

                    {/* Scheduler Jobs Panel (only for scheduler category) */}
                    {category === "scheduler" && <SchedulerJobsPanel />}
                </div>
            )}
        </div>
    );
}
